var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { debounce } from '@core-front/helper';
import { useHorizontalScroll } from '@core-front/horizontal-scroll';
import { TabsContext } from '../tabs/tabs-core';
const TabsNavCore = (props) => {
    const { components, elementRef, leftFormatter, widthFormatter, items: itemsProp, children, className, onKeyDown, items, 'aria-labelledby': labelledBy } = props, rest = __rest(props, ["components", "elementRef", "leftFormatter", "widthFormatter", "items", "children", "className", "onKeyDown", "items", 'aria-labelledby']);
    const { TabsNavItem, ScrollFade } = props.components;
    const scrollContainerRef = React.useRef(null);
    const elementsRef = React.useRef([]);
    const innerElement = React.useRef(null);
    const activeElement = React.useRef(null);
    const underline = React.useRef();
    const [animatingUnderline, setAnimatingUnderline] = React.useState(false);
    const timeout = 0;
    let activeItem = null;
    const { iconPosition } = React.useContext(TabsContext);
    const BEM = () => {
        const classArray = ['tabs__nav'];
        if (iconPosition) {
            classArray.push(`tabs__nav--icons-${iconPosition}`);
        }
        if (props.className) {
            classArray.push(props.className);
        }
        return classArray.join(' ');
    };
    const underlineBEM = () => {
        const classArray = ['tabs__nav-underline'];
        if (underline.current && underline.current.type) {
            classArray.push('tabs__nav-underline--type-' + underline.current.type);
        }
        if (animatingUnderline) {
            classArray.push('is-animating');
        }
        return classArray.join(' ');
    };
    const getUnderlineStyle = () => {
        return {
            maxWidth: underline.current && !Number.isNaN(underline.current.width) ? underline.current.width : 0,
            left: underline.current && underline.current.left,
        };
    };
    const updateUnderline = React.useCallback((item, isResizing = false) => {
        var _a;
        if (activeElement.current) {
            const style = getComputedStyle(activeElement.current);
            const newUnderlineProps = {
                width: widthFormatter ? widthFormatter(activeElement) : parseFloat(style.width) - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
                left: leftFormatter ? leftFormatter(activeElement, innerElement) : activeElement.current.offsetLeft,
                type: item ? item.type : (_a = underline.current) === null || _a === void 0 ? void 0 : _a.type,
            };
            if (underline.current !== newUnderlineProps) {
                underline.current = newUnderlineProps;
                if (!isResizing) {
                    setAnimatingUnderline(true);
                    window.clearTimeout(timeout);
                    window.setTimeout(() => {
                        setAnimatingUnderline(false);
                    }, 300);
                }
            }
        }
    }, [widthFormatter, leftFormatter]);
    const setActiveElement = (element, item) => {
        if (element && activeElement.current !== element && item.current) {
            activeElement.current = element;
            activeItem = item;
            if (innerElement.current) {
                updateUnderline(activeItem);
            }
        }
    };
    const resizeHandler = (isResizing) => {
        updateUnderline(undefined, isResizing);
    };
    const debouncedResizeHandler = debounce(resizeHandler.bind(this, true), 100);
    React.useEffect(() => {
        window.addEventListener('resize', debouncedResizeHandler);
        return () => {
            window.removeEventListener('resize', debouncedResizeHandler);
            debouncedResizeHandler.clear();
        };
    });
    const handleClick = (item, event) => {
        event.preventDefault();
        if (item.onClick) {
            item.onClick(event);
        }
    };
    const handleKeyUp = (event) => {
        event.preventDefault();
        event.stopPropagation();
        let tabs = [];
        if (innerElement.current instanceof Element) {
            tabs = Array.from(innerElement.current.querySelectorAll('[role="tab"]'));
        }
        const focusPosition = document.activeElement ? tabs.indexOf(document.activeElement) : 0;
        switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
                if (focusPosition > 0) {
                    focusTab(focusPosition - 1, tabs);
                }
                else {
                    focusTab(tabs.length - 1, tabs);
                }
                break;
            case 'ArrowRight':
            case 'ArrowDown':
                if (focusPosition < tabs.length - 1) {
                    focusTab(focusPosition + 1, tabs);
                }
                else {
                    focusTab(0, tabs);
                }
                break;
            case 'Home':
                focusTab(0, tabs);
                break;
            case 'End':
                focusTab(tabs.length - 1, tabs);
                break;
            case ' ':
            case 'Enter': {
                if (event.target instanceof HTMLElement) {
                    const tab = event.target;
                    tab.click();
                }
                break;
            }
        }
    };
    const focusTab = (index, tabs) => {
        const element = tabs[index];
        element.focus();
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
        });
    };
    const handleKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
            case 'ArrowRight':
            case 'ArrowDown':
            case ' ':
                // prevent document scroll when navigating with arrow keys or selecting tab with space
                event.preventDefault();
                break;
        }
        if (props.onKeyDown) {
            props.onKeyDown(event);
        }
    };
    React.useEffect(() => {
        var _a;
        if ((_a = props.elementRef) === null || _a === void 0 ? void 0 : _a.current) {
            innerElement.current = props.elementRef.current;
        }
    }, [props.elementRef]);
    React.useEffect(() => {
        if (activeElement.current && innerElement.current && activeItem) {
            updateUnderline(activeItem);
        }
    }, [activeElement, innerElement, activeItem, updateUnderline]);
    const { scrollTo } = useHorizontalScroll({
        containerRef: scrollContainerRef,
        elementsRef,
    });
    return (React.createElement("div", Object.assign({ className: BEM(), onKeyUp: handleKeyUp, onKeyDown: handleKeyDown }, rest),
        !!items.length && (React.createElement(ScrollFade, { className: "tabs__nav-scroll", hasArrows: { enabled: true, focusable: false }, elementRef: el => (scrollContainerRef.current = el), onArrowClick: (_, direction) => scrollTo(direction) },
            React.createElement("div", { className: "tabs__nav-scroll-inner" },
                underline && React.createElement("span", { className: underlineBEM(), style: getUnderlineStyle() }),
                React.createElement("ul", { className: "tabs__nav-inner", ref: innerElement, role: "tablist", "aria-labelledby": labelledBy }, items.map((item, index) => (React.createElement(TabsNavItem, Object.assign({}, item, { onClick: event => handleClick(item, event), key: item.id || index, tabIndex: item.current ? 0 : -1, onlyChild: index === 0 && items.length === 1, "aria-selected": item.current, elementRef: element => {
                        setActiveElement(element, item);
                        elementsRef.current[index] = element;
                    } })))))))),
        props.children));
};
export { TabsNavCore as default };
